// Описание звуков игры и движок, который их играет.
//
// Звуки лежат атласами: один файл держит их все подряд, а json рядом с ним говорит, с какой
// миллисекунды начинается каждый звук и сколько он длится. Атласа два — музыка и эффекты,
// см. assets/catalog.ts.
//
// Файл со звуком записан в четырёх форматах, а грузится один: какой из них браузер умеет,
// знает только он сам, см. sound/howlerEngine.ts.
//
// Здесь лежит одно описание. Сам движок браузерный и в тестах не заводится, поэтому игра
// говорит с ним через SoundEngine: что и когда играет, проверяется на подставном движке,
// см. tests/sound/sound.test.ts.

/**
 * Звук атласа: его ключ, а при нужде и вырезанный из него кусок.
 *
 * Кусок вырезают там, где звук идёт вместе с картинкой: паровоз записан длинной дорожкой,
 * а клуб пара из-под котла живёт секунды, см. ui/smoke.ts.
 *
 * Берётся кусок не с начала дорожки, а откуда сказано: дорожка паровоза начинается тихим
 * разгоном, и гудок на ней стоит секундой позже, см. ADR-49.
 *
 * Нет поля seconds — звук идёт до конца своей дорожки. Поле looped гонит его по кругу:
 * так идёт стук колёс на ходу, а конца у хода нет, см. ui/ride.ts.
 */
export type SoundPick = string | { readonly key: string; readonly from?: number; readonly seconds?: number; readonly looped?: boolean };

/** Ключ звука внутри атласа. */
export function soundKey(pick: SoundPick): string {
  return typeof pick === 'string' ? pick : pick.key;
}

/** Описание атласа звуков: адрес его json и нужные игре звуки. */
export interface SoundSpriteSource<TName extends string> {
  /** Адрес json-атласа. Файлы со звуком лежат рядом с ним, и их имена он называет сам. */
  readonly url: string;

  /** Ключ в sounds — имя звука в коде, значение — его ключ внутри атласа. */
  readonly sounds: Readonly<Record<TName, SoundPick>>;

  /** Играют ли эти звуки по кругу. Так идёт музыка: дорожка тянется, пока её не сменят. */
  readonly looped?: boolean;

  /** Громкость от 0 до 1. Нет поля — звук идёт в полную силу. */
  readonly volume?: number;
}

/** Json-атлас с диска: имена файлов со звуком и место каждого звука внутри них. */
export interface SoundAtlas {
  /** Один и тот же звук в разных форматах. Браузер берёт первый, который умеет. */
  readonly urls: readonly string[];

  /** Ключ звука, за ним начало в миллисекундах и длительность там же. */
  readonly sprite: Readonly<Record<string, readonly number[]>>;
}

/** Место звука внутри файла: начало, длительность и то, играть ли его по кругу. */
export type SoundSpan = [number, number, boolean];

/** Звуки атласа под именами из кода. */
export type SoundSprite = Record<string, SoundSpan>;

/**
 * Куда грузятся атласы звуков.
 *
 * Грузит их менеджер ассетов вместе с картинками, одним набором и одной полосой загрузки,
 * см. assets/manager.ts. Про звук он знает ровно эту строку.
 */
export interface SoundLoader {
  /** Грузит атлас и ждёт, пока файл со звуком будет разобран. */
  load(source: SoundSpriteSource<string>): Promise<void>;
}

/**
 * Играющий звук.
 *
 * За эту ручку звук уводят, когда картинка кончилась раньше него: показ оборвали, а звук
 * остался бы играть за пустым экраном, см. ADR-50.
 */
export interface SoundVoice {
  /** Уводит звук на нет за заданное время и глушит его. */
  fadeOut(seconds: number): void;
}

/** Движок звука: он и есть всё, чем игра шумит. */
export interface SoundEngine {
  /**
   * Играет звук один раз и отдаёт ручку, которой его можно увести.
   *
   * Null — звука нет: имя незнакомое или атлас не загрузился. Звать за ручку тогда некого,
   * и зовущий проверяет её на null, а не гадает.
   */
  play(name: string): SoundVoice | null;

  /**
   * Сколько звук длится в секундах. Ноль — звук не загружен.
   *
   * По этой длине картинка идёт со звуком в ногу: дым держится ровно столько, сколько гудит
   * паровоз, см. ui/smoke.ts. Длина берётся у звука, а не задаётся второй раз в коде.
   */
  seconds(name: string): number;

  /** Ставит зацикленную дорожку вместо нынешней. Null — тишина. */
  loop(name: string | null): void;

  /** Глушит и возвращает весь звук разом. Дорожка при этом идёт дальше, её просто не слышно. */
  mute(muted: boolean): void;

  /** Пускает звук после жеста игрока: до него браузер держит его закрытым. */
  resume(): void;
}

/** Движок, который ничего не играет. С ним игра идёт до загрузки звука и в тестах. */
export const SILENT_ENGINE: SoundEngine = {
  play: (): SoundVoice | null => null,
  seconds: (): number => 0,
  loop: (): void => {},
  mute: (): void => {},
  resume: (): void => {},
};

/**
 * Адреса файлов атласа.
 *
 * Имена файлов лежат в самом json, а папка у них та же, что у него: художник и звукарь кладут
 * атлас одной пачкой. Поэтому имена в каталоге игры не повторяются — их незачем править дважды.
 */
export function soundUrls(url: string, atlas: SoundAtlas): string[] {
  const folder: string = url.slice(0, url.lastIndexOf('/') + 1);

  return atlas.urls.map((name: string) => `${folder}${name}`);
}

/**
 * Укорочен ли звук в каталоге. Укороченный кончается там, где дорожка ещё звучит.
 *
 * Начало куска хвоста не трогает: сдвинутый вправо кусок кончается тем же затуханием,
 * каким кончалась дорожка.
 */
export function soundCut(pick: SoundPick): boolean {
  return typeof pick !== 'string' && pick.seconds !== undefined;
}

/** Играет ли звук по кругу. Своё поле у звука сильнее общего поля атласа. */
function soundLooped(pick: SoundPick, source: SoundSpriteSource<string>): boolean {
  if (typeof pick !== 'string' && pick.looped !== undefined)
    return pick.looped;

  return source.looped ?? false;
}

/**
 * Раскладывает объявленные звуки по именам из кода.
 *
 * Имя звука в игре своё, а не ключ атласа: ключи там записаны через точку и приставку,
 * и таскать их по коду значило бы переписывать игру вслед за звукарём.
 *
 * Вырезанный кусок не выходит за края своего звука: за ними в файле лежит соседний.
 *
 * @throws Error если объявленного звука в атласе нет или кусок вырезан за его концом
 */
export function soundSprite(source: SoundSpriteSource<string>, atlas: SoundAtlas): SoundSprite {
  const sprite: SoundSprite = {};

  for (const [name, pick] of Object.entries<SoundPick>(source.sounds)) {
    const key: string = soundKey(pick);
    const span: readonly number[] | undefined = atlas.sprite[key];
    if (span === undefined)
      throw new Error(`В атласе ${source.url} нет звука "${key}"`);

    const from: number = typeof pick === 'string' ? 0 : (pick.from ?? 0) * 1000;
    const asked: number = typeof pick === 'string' ? Infinity : (pick.seconds ?? Infinity) * 1000;

    const left: number = span[1] - from;
    if (left <= 0)
      throw new Error(`Звук "${key}" в атласе ${source.url} длится ${span[1]}мс, а кусок вырезан с ${from}мс`);

    sprite[name] = [span[0] + from, Math.min(left, asked), soundLooped(pick, source)];
  }

  return sprite;
}
